import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useUser } from '../contexts/UserContext'

interface ChatLayoutPreferences {
    [pageKey: string]: {
        chatWidth?: number
        leftChatWidth?: number
        rightChatWidth?: number
    }
}

/**
 * Hook to manage chat panel layout preferences per page
 * @param pageKey - Unique identifier for the page (e.g., 'agent_profile', 'agent_config')
 * @param chatPosition - Position of the chat ('left' | 'right' | 'center')
 * @param defaultWidth - Default width in pixels
 */
export function useChatLayout(
    pageKey: string,
    chatPosition: 'left' | 'right' | 'center' = 'right',
    defaultWidth: number = 400
) {
    const { user } = useUser()
    const [chatWidth, setChatWidth] = useState(defaultWidth)
    const [isLoading, setIsLoading] = useState(true)

    // Load chat width from database
    useEffect(() => {
        async function loadChatWidth() {
            if (!user?.id) {
                setIsLoading(false)
                return
            }

            try {
                const { data, error } = await supabase
                    .from('user_preferences')
                    .select('chat_layout_preferences')
                    .eq('user_id', user.id)
                    .single()

                if (error) {
                    console.error('Error loading chat layout preferences:', error)
                    setIsLoading(false)
                    return
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if ((data as any)?.chat_layout_preferences) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const preferences = (data as any).chat_layout_preferences as ChatLayoutPreferences
                    const pagePrefs = preferences[pageKey]

                    if (pagePrefs) {
                        const widthKey = chatPosition === 'left'
                            ? 'leftChatWidth'
                            : chatPosition === 'right'
                                ? 'rightChatWidth'
                                : 'chatWidth'

                        const savedWidth = pagePrefs[widthKey]
                        if (savedWidth) {
                            setChatWidth(savedWidth)
                        }
                    }
                }
            } catch (error) {
                console.error('Error loading chat width:', error)
            } finally {
                setIsLoading(false)
            }
        }

        loadChatWidth()
    }, [user?.id, pageKey, chatPosition])

    // Save chat width to database (debounced)
    const saveChatWidth = useCallback(
        async (newWidth: number) => {
            if (!user?.id) return

            try {
                // First, get current preferences
                const { data: currentData } = await supabase
                    .from('user_preferences')
                    .select('chat_layout_preferences')
                    .eq('user_id', user.id)
                    .single()

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const currenttypedData = currentData as any
                const currentPrefs = (currenttypedData?.chat_layout_preferences || {}) as ChatLayoutPreferences

                // Update the specific page and position
                const widthKey = chatPosition === 'left'
                    ? 'leftChatWidth'
                    : chatPosition === 'right'
                        ? 'rightChatWidth'
                        : 'chatWidth'

                const updatedPrefs = {
                    ...currentPrefs,
                    [pageKey]: {
                        ...currentPrefs[pageKey],
                        [widthKey]: newWidth
                    }
                }

                // Save to database
                const { error } = await supabase
                    .from('user_preferences')
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .update({ chat_layout_preferences: updatedPrefs } as any)
                    .eq('user_id', user.id)

                if (error) {
                    console.error('Error saving chat width:', error)
                }
            } catch (error) {
                console.error('Error saving chat width:', error)
            }
        },
        [user?.id, pageKey, chatPosition]
    )

    // Update width with debounce
    const updateChatWidth = useCallback(
        (newWidth: number) => {
            setChatWidth(newWidth)

            // Debounce save to database
            const timeoutId = setTimeout(() => {
                saveChatWidth(newWidth)
            }, 500) // Wait 500ms after user stops resizing

            return () => clearTimeout(timeoutId)
        },
        [saveChatWidth]
    )

    return {
        chatWidth,
        setChatWidth: updateChatWidth,
        isLoading
    }
}
